"use client";

import { useCallback, useEffect, useRef } from "react";
import { Tokens } from "./types/tokens";
import { TokensInfo } from "../auth/auth-context";
import { isTokenExpired } from "../auth/tokenExpiry";
import {
    FetchBaseOptions,
    FetchInputType,
    FetchInitType,
} from "./types/fetch-params";
import useUserId from "~/hooks/useUserId";
import { useLogEvent } from "~/hooks/useLogEvent";
import {
    detectVercelInterstitial,
    toProxyUrl,
    stringifyInput,
    isSafeHttpMethod,
    proxyRetryCredentials,
} from "./vercel-proxy";
import { getInAppWebview } from "./detect-webview";
import { buildRetryRequestInit } from "./build-request-init";
import {
    logProxyRetryResult,
    logProxyVia,
    PROXY_RETRY_RESULT,
} from "./log-proxy-via";
import { classifyFetchFailure, logFetchCancellation } from "./fetch-failure";
import { sendRequestBeacon, sendErrorBeacon } from "./web-event-beacon";
import {
    describeError,
    redactPersonalData,
} from "~/utils/errorTelemetry";

const fetch = require("fetch-retry")(global.fetch);

function useFetchBase() {
    const { userId } = useUserId();
    const userIdRef = useRef(userId);
    // Datadog logger per project contract (CLAUDE.md → "Client-side logging
    // convention"). The hook gates emission on AppConfig.isProduction
    // internally; non-prod observability is mirrored to console.* below.
    const logEvent = useLogEvent();

    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    return useCallback(
        async (
            input: FetchInputType,
            init?: FetchInitType,
            tokens?: Tokens & {
                setTokensInfo?: (tokensInfo: TokensInfo) => void;
            },
            options?: FetchBaseOptions
        ) => {
            let headers: HeadersInit = {
                "x-custom-lang": "en",
            };

            if (!(init?.body instanceof FormData)) {
                headers = {
                    ...headers,
                    "Content-Type": "application/json",
                };
            }

            // Checked before the header is built: a token past its expiry is
            // dropped from the session and never sent, so the request goes out
            // as a guest instead of earning a certain 401 on a stale bearer.
            if (isTokenExpired(tokens)) {
                tokens?.setTokensInfo?.(null);
            } else if (tokens?.token) {
                headers = {
                    ...headers,
                    Authorization: `Bearer ${tokens.token}`,
                };
            }

            sendRequestBeacon(input, init);

            const userAgent =
                typeof navigator !== "undefined" ? navigator.userAgent : undefined;

            const requestInit = buildRetryRequestInit({
                init,
                headers,
                userAgent,
            });

            // Tracks whether we've already attempted the same-origin proxy
            // path in this invocation. Prevents both:
            //   • re-entry into the catch-side fallback when the in-try
            //     proxy retry itself throws (would otherwise double-fire)
            //   • infinite recursion if the proxy is consistently failing
            let alreadyRetriedViaProxy = false;

            try {
                const response: Response = await fetch(input, requestInit);

                const interstitial = detectVercelInterstitial(response);
                if (!interstitial) {
                    return response;
                }

                // Cross-origin call intercepted by Vercel (DDoS challenge or
                // Deployment Protection). Retry once via the same-origin path
                // so vercel.json routes applies the bypass header.
                const proxyUrl = toProxyUrl(input);
                if (!proxyUrl) return response;

                const challengeData = {
                    interstitial,
                    original_url: stringifyInput(input),
                    proxy_url: proxyUrl,
                    href: window.location.href,
                    userAgent: navigator.userAgent,
                    user_id: userIdRef.current,
                };
                logEvent({
                    message: `Vercel ${interstitial} detected — retrying via vercel.json proxy`,
                    tag: "api-interstitial-retry",
                    data: challengeData,
                });
                if (process.env.NEXT_PUBLIC_ENV !== "production") {
                    console.warn(`[ib-proxy] ${interstitial} detected, retrying via /api/v1/*`, challengeData);
                }

                alreadyRetriedViaProxy = true;
                // Override credentials for the proxy retry: the original
                // direct call was cross-origin (no cookies sent by default);
                // switching to same-origin would silently start sending
                // first-party cookies to the backend via the proxy. See
                // `proxyRetryCredentials` for the full rationale.
                const retried = await fetch(proxyUrl, {
                    ...requestInit,
                    credentials: proxyRetryCredentials(init),
                });
                logProxyVia({
                    response: retried,
                    proxyUrl,
                    reason: interstitial,
                    userId: userIdRef.current,
                    logEvent,
                });
                return retried;
            } catch (error) {
                // Classify WHY the request failed (timeout / offline / aborted /
                // network) so Datadog shows a real reason instead of a generic
                // message — and so we can suppress benign cancellations here.
                const failureReason = classifyFetchFailure(error);

                // Benign client-side cancellation / no connectivity. Neither is
                // a real (server-side) error:
                //   • "aborted"  — the user left the page or the app superseded
                //     the request (component unmount, debounced refetch).
                //   • "offline"  — the device has no connectivity; the request
                //     never reached the backend.
                // The request still completes fine on the server when it does
                // run, so retrying is pointless and reporting it only inflates
                // the error stream with noise. Keep a low-noise Datadog
                // breadcrumb (`api-request-cancelled`, info level) for volume
                // visibility, but NEVER fire the web error beacon. Our own 15s
                // deadline surfaces as "timeout" and is handled at the terminal
                // block below (after the recovery attempt).
                if (failureReason === "aborted" || failureReason === "offline") {
                    logFetchCancellation({
                        logEvent,
                        error,
                        failureReason,
                        input,
                        userAgent,
                        userId: userIdRef.current,
                    });
                    return;
                }

                // Catch-side network fallback (Linear #2568):
                // When Vercel's DDoS challenge or Deployment Protection page
                // is returned WITHOUT CORS headers covering our origin, the
                // browser blocks the Response before JS can inspect it and
                // `fetch()` rejects with `TypeError: Failed to fetch`.
                // `detectVercelInterstitial` never runs, the in-try retry is
                // bypassed, and we'd silently return `undefined` — the exact
                // failure mode this PR fixes for the Response path.
                //
                // Browsers deliberately don't distinguish CORS rejection
                // from genuine network errors (security), so we recover for
                // any throw as long as ALL of these hold:
                //   • not already retried (avoids in-try-then-catch loop)
                //   • URL maps to a known upstream origin (toProxyUrl)
                //   • HTTP method is SAFE (GET/HEAD/OPTIONS) — for unsafe
                //     methods the backend may have already executed before
                //     the network error surfaced; retrying could double-charge,
                //     duplicate emails, etc. Better to surface the error than
                //     risk a duplicate side effect (`isSafeHttpMethod`), unless
                //     the caller opted in with `retryUnsafeMethodViaProxy`
                //     because a duplicate is harmless for that endpoint.
                const recoveryUrl = !alreadyRetriedViaProxy ? toProxyUrl(input) : null;
                const isRetryableMethod =
                    isSafeHttpMethod(init?.method) ||
                    options?.retryUnsafeMethodViaProxy === true;
                const canSafelyRetry = recoveryUrl && isRetryableMethod;

                const { errorName, errorMessage: loggableMessage } =
                    describeError(error);
                if (canSafelyRetry) {
                    const retryAttempt = {
                        proxyUrl: recoveryUrl,
                        originalUrl: stringifyInput(input),
                        method: (init?.method ?? "GET").toUpperCase(),
                        userId: userIdRef.current,
                        logEvent,
                    };
                    try {
                        const fallbackData = {
                            original_url: retryAttempt.originalUrl,
                            proxy_url: recoveryUrl,
                            method: retryAttempt.method,
                            original_error: loggableMessage,
                            href: window.location.href,
                            user_id: userIdRef.current,
                        };
                        logEvent({
                            message: "Direct fetch threw (likely CORS-blocked interstitial or network failure) — retrying via vercel.json proxy",
                            tag: "api-network-fallback",
                            data: fallbackData,
                        });
                        if (process.env.NEXT_PUBLIC_ENV !== "production") {
                            console.warn("[ib-proxy] direct fetch threw, retrying via /api/v1/*", fallbackData);
                        }

                        const recovered = await fetch(recoveryUrl, {
                            // Rebuild the init so the recovery gets a FRESH
                            // abort signal. Reusing `requestInit` would carry
                            // over the original signal, which may already be
                            // aborted (e.g. the direct attempt hit our timeout)
                            // — making this recovery an instant no-op instead of
                            // a real retry.
                            ...buildRetryRequestInit({ init, headers, userAgent }),
                            credentials: proxyRetryCredentials(init),
                        });
                        logProxyVia({
                            response: recovered,
                            proxyUrl: recoveryUrl,
                            reason: "network-error",
                            userId: userIdRef.current,
                            logEvent,
                        });
                        logProxyRetryResult({
                            ...retryAttempt,
                            result: PROXY_RETRY_RESULT.RECOVERED,
                            status: recovered.status,
                        });
                        return recovered;
                    } catch (proxyError) {
                        logProxyRetryResult({
                            ...retryAttempt,
                            result: PROXY_RETRY_RESULT.FAILED,
                            proxyError: describeError(proxyError).errorMessage,
                            proxyFailureReason: classifyFetchFailure(proxyError),
                        });
                        // Proxy also failed — fall through to the standard
                        // error path so the ORIGINAL error is what gets
                        // logged and reported (more useful for debugging).
                    }
                } else if (recoveryUrl && !isRetryableMethod) {
                    // Observability: track how often unsafe-method requests
                    // hit network errors that we COULD have retried but
                    // skipped for safety. Tag separate from `api-error` so
                    // it does not pollute the error baseline.
                    logEvent({
                        message: "Direct fetch threw on unsafe HTTP method — skipping catch-side proxy retry to avoid duplicate side effects",
                        tag: "api-network-fallback-skipped-unsafe-method",
                        data: {
                            original_url: stringifyInput(input),
                            method: (init?.method ?? "GET").toUpperCase(),
                            original_error: loggableMessage,
                            href: window.location.href,
                            user_id: userIdRef.current,
                        },
                    });
                }

                // Filter benign client-side noise OUT of the error beacon so
                // `ods.prod_web_errors` only ever receives REAL, unrecovered
                // transport failures:
                //   • SAFE-method "timeout" — our OWN deadline fired on a
                //     GET/HEAD/OPTIONS. The request typically still completes
                //     on the server and the call auto-retries; cutting it off
                //     client-side is not a server error (this is the iOS /
                //     in-app-webview noise we are suppressing).
                //   • non-`Error` / message-less throw — WebKit surfaces some
                //     aborts as a reason that serializes to `{}` / has no
                //     message. That is a cancellation artefact, not a failure.
                // For these we keep only the low-noise `api-request-cancelled`
                // breadcrumb (info) and do NOT fire the web beacon.
                //
                // An UNSAFE-method timeout (e.g. create-payment-intent /
                // process-checkout POST) is deliberately NOT benign: it is a
                // torn-off, ambiguous attempt ("charged? not charged?") the
                // business needs to see, so it falls through to the `api-error`
                // + beacon path below.
                const isBenignTimeout =
                    failureReason === "timeout" && isSafeHttpMethod(init?.method);
                const isCancellationArtefact =
                    !(error instanceof Error) || !error.message;
                if (isBenignTimeout || isCancellationArtefact) {
                    logFetchCancellation({
                        logEvent,
                        error,
                        failureReason,
                        input,
                        userAgent,
                        userId: userIdRef.current,
                    });
                    return;
                }

                // Genuine, unrecovered network failure (e.g. backend
                // unreachable, `TypeError: Failed to fetch` that the proxy
                // fallback could not recover). This IS a real error.
                // `failureReason` lets Datadog filter by WHY it failed and
                // `in_app_webview` correlates failures with slow FB/IG webviews.
                logEvent({
                    message: loggableMessage,
                    tag: "api-error",
                    // Deliberately left at the default `info`. Levelling this
                    // by HTTP method was tried and reverted: 85% of the
                    // unsafe-method failures here are the fire-and-forget
                    // `sendUnauthorizedEvent` analytics beacon, not a payment,
                    // so "error" would have described ~2,900 records a week
                    // that carry no ambiguity about whether a customer was
                    // charged. Level this per endpoint, not per method, if it
                    // is revisited.
                    data: {
                        failure_reason: failureReason,
                        in_app_webview: getInAppWebview(userAgent),
                        error_name: errorName,
                        // The frames are the point and they survive redaction;
                        // V8 prefixes the raw message to them, which does not.
                        error_stack:
                            error instanceof Error && error.stack
                                ? redactPersonalData(error.stack)
                                : undefined,
                        url: input,
                        href: window.location.href,
                        userAgent: navigator.userAgent,
                        user_id: userIdRef.current,
                    }
                });

                // The raw message, unlike the Datadog log above. This beacon
                // feeds `ods.prod_web_errors`, a BigQuery table inside our own
                // perimeter whose rows are grouped and joined on the exact
                // wording by analytics outside this repo. Redacting it here
                // would silently rewrite a dataset this change has no mandate
                // to touch; the third-party vendor is the case redaction
                // exists for.
                if (!options?.skipErrorBeacon) {
                    sendErrorBeacon(
                        input,
                        error instanceof Error ? error.message : String(error)
                    );
                }
            }
        },
        [logEvent]
    );
}

export default useFetchBase;
